import { Link } from "react-router-dom";
import { ArrowUpRight, MessageCircle, Phone, Star } from "lucide-react";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import SectionHeader from "@/components/SectionHeader";
import FaqSection from "@/components/FaqSection";
import LeadForm from "@/components/LeadForm";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { SITE, STATS, PROCESS, WHY_US, TESTIMONIALS, FAQS } from "@/data/site";
import { SERVICES, SERVICE_IMAGES } from "@/data/services";
import { POSTS } from "@/data/posts";

const HERO_IMG = "/saivu-hero.jpg";
const WHY_IMG = "/saivu-industries.jpg";

function Hero() {
  return (
    <section className="bg-mist pt-28 pb-14 sm:pt-32" data-testid="hero-section">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-6 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="font-mono text-xs font-medium uppercase tracking-[0.2em] text-gold-deep sm:text-sm">
              Professional Consulting · Finance · Tax · Compliance · Advisory
            </p>
            <h1 className="mt-5 text-4xl font-bold leading-[1.15] tracking-tight text-navy-900 sm:text-5xl">
              Your business journey, our professional support.
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-ink sm:text-lg">
              SAIVU Consulting Private Limited is a professional consulting company serving
              startups, MSMEs and professionals across India — with a multidisciplinary team
              that includes Chartered Accountants, MBA Finance professionals, legal
              professionals and business consultants.
            </p>
          </Reveal>
          <Reveal delay={0.12} className="mt-8 flex flex-wrap items-center gap-3.5">
            <Link
              to="/contact"
              data-testid="hero-consultation-button"
              className="rounded-md bg-navy-900 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-navy-800"
            >
              Book a free 20-min consultation
            </Link>
            <a
              href={SITE.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              className="inline-flex items-center gap-2 rounded-md border border-navy-100 bg-white px-6 py-3 text-sm font-medium text-navy-900 transition-colors hover:border-navy-900"
            >
              <MessageCircle className="h-4 w-4 text-emerald-600" />
              WhatsApp us
            </a>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-7 font-mono text-xs uppercase tracking-[0.14em] text-slate-600">
              50-50 payment terms · Fixed fees · PAN-India + NRI clients
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-5">
          <Reveal delay={0.1}>
            <img
              src={HERO_IMG}
              alt="SAIVU Consulting consultation — senior consultant with two clients at the meeting table, SAIVU logo and service list on the office wall"
              className="aspect-[2/3] w-full rounded-lg border border-line object-cover shadow-card"
              fetchPriority="high"
            />
          </Reveal>
        </div>
      </div>

      <div className="mx-auto mt-14 max-w-7xl px-5 sm:px-6" data-testid="stats-strip">
        <div className="grid grid-cols-2 gap-6 border-t border-line pt-8 md:grid-cols-4">
          {STATS.map((s) => (
            <Reveal key={s.label}>
              <p className="text-2xl font-bold text-navy-900 sm:text-3xl" data-testid={`stat-value-${s.label.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
                {s.value}
              </p>
              <p className="mt-1 text-xs font-medium uppercase tracking-[0.12em] text-slate-600">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServicesSection() {
  return (
    <section id="services" className="bg-white py-20" data-testid="services-bento-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeader
            index="01"
            eyebrow="Our services"
            title="15 Services. 1 Consulting Company."
            sub="Business, finance, tax, compliance, legal-support and advisory services — delivered by the SAIVU team of qualified professionals."
          />
          <Reveal delay={0.15}>
            <Link
              to="/services"
              className="link-underline inline-flex items-center gap-2 pb-0.5 text-sm font-semibold text-navy-900"
              data-testid="bento-view-all-link"
            >
              View all services <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>

        <Stagger className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3" gap={0.05}>
          {SERVICES.map((s) => (
            <StaggerItem key={s.slug} className="h-full">
              <Link
                to={`/services/${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className="group flex h-full flex-col rounded-lg border border-line bg-white p-4 transition-all duration-200 hover:border-navy-100 hover:shadow-lift"
              >
                <img
                  src={SERVICE_IMAGES[s.slug]}
                  alt={`${s.title} — professional service by SAIVU`}
                  className="h-28 w-full rounded-md object-cover sm:h-32"
                  loading="lazy"
                />
                <span className="mt-4 font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-slate-600">
                  {s.category}
                </span>
                <h3 className="mt-2 text-lg font-semibold text-navy-900">{s.title}</h3>
                <p className="mt-2 flex-1 text-[15px] leading-relaxed text-ink">{s.short}</p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-navy-800">
                  Learn more
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>

        <Reveal className="mt-10">
          <div className="rounded-lg border border-line bg-mist p-6 sm:p-7" data-testid="fee-disclaimer">
            <p className="text-base font-semibold text-navy-900">Fee Disclaimer</p>
            <p className="mt-2 text-[15px] leading-relaxed text-ink">
              The fees mentioned for our services are indicative range references only. The
              final professional fee/quotation may vary depending on the nature, scope and
              specific requirements of the engagement.{" "}
              <strong className="font-semibold text-navy-900">
                Please consult or speak with our office to confirm the final fee/quotation
                before making any payment.
              </strong>
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function ProcessSection() {
  return (
    <section className="bg-mist py-20" data-testid="process-section">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-6 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <SectionHeader
              index="02"
              eyebrow="How it works"
              title="A process you can set your watch by."
              sub="Five steps, written down before we start. No surprises is not a slogan — it is our operating model."
            />
            <Reveal delay={0.2} className="mt-8">
              <div className="rounded-lg border border-line border-l-4 border-l-navy-900 bg-white p-6 shadow-card" data-testid="payment-terms-card">
                <p className="font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-gold-deep">Payment terms</p>
                <p className="mt-3 text-lg font-semibold text-navy-900">
                  50% before commencement · 50% after completion
                </p>
                <p className="mt-2 text-sm leading-relaxed text-ink">
                  You review the deliverables before the balance is paid. Fair for you,
                  fair for us.
                </p>
                <Link
                  to="/contact"
                  className="mt-5 inline-flex items-center gap-2 rounded-md bg-navy-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-navy-800"
                  data-testid="payment-terms-cta"
                >
                  Book free consult
                </Link>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="lg:col-span-7">
          <Stagger gap={0.08}>
            {PROCESS.map((p) => (
              <StaggerItem key={p.num}>
                <div className="flex gap-6 border-b border-line py-7 first:pt-0">
                  <span className="w-8 shrink-0 pt-1 font-mono text-sm font-semibold text-gold-deep">{p.num}</span>
                  <div>
                    <h3 className="text-lg font-semibold text-navy-900">{p.title}</h3>
                    <p className="mt-1.5 max-w-lg text-[15px] leading-relaxed text-ink">{p.desc}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </div>
    </section>
  );
}

function WhyUs() {
  return (
    <section className="bg-white py-20" data-testid="why-us-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-6">
        <SectionHeader
          index="03"
          eyebrow="Why SAIVU"
          title="The finance partner clients don't leave."
          sub="We keep clients for the long run by putting fees, timelines and deliverables in writing — before the work begins."
        />
        <div className="mt-12 grid items-start gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <img
              src={WHY_IMG}
              alt="Industries SAIVU supports — car rental, manpower services, Ayurveda, restaurants and food business, manufacturing, and IT and technology companies"
              className="w-full rounded-lg border border-line shadow-card"
              loading="lazy"
            />
            <p className="mt-4 text-sm leading-relaxed text-slate-600" data-testid="why-us-caption">
              Industries we support — from car rental, staffing and Ayurveda to hospitality,
              manufacturing, retail and technology.
            </p>
            <div className="mt-5 inline-flex items-center gap-2.5 rounded-lg border border-navy-100 bg-navy-50 px-4 py-2.5" data-testid="why-us-badge">
              <span className="h-2 w-2 rounded-full bg-gold" />
              <span className="text-sm font-medium text-navy-900">
                500+ active clients · 22 states
              </span>
            </div>
          </Reveal>
          <div className="grid gap-x-10 gap-y-8 sm:grid-cols-2 lg:col-span-7">
            {WHY_US.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.05}>
                <p className="font-mono text-sm font-semibold text-gold-deep">0{i + 1}</p>
                <h3 className="mt-2 text-base font-semibold text-navy-900 sm:text-lg">{w.title}</h3>
                <p className="mt-1.5 text-[15px] leading-relaxed text-ink">{w.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="bg-mist py-20" data-testid="testimonials-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-6">
        <SectionHeader
          index="04"
          eyebrow="Client voices"
          title="Businesses that trust us with their compliance."
          sub="Rated 4.9 out of 5 on Google by the businesses we serve."
        />
        <Stagger className="mt-12 grid gap-5 md:grid-cols-2" gap={0.07}>
          {TESTIMONIALS.map((t) => (
            <StaggerItem key={t.name} className="h-full">
              <figure className="flex h-full flex-col rounded-lg border border-line bg-white p-7 shadow-card">
                <div className="flex gap-0.5 text-gold" aria-label="5 star review">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4" fill="currentColor" strokeWidth="0" />
                  ))}
                </div>
                <blockquote className="mt-4 flex-1 text-[15px] leading-relaxed text-navy-900">
                  “{t.quote}”
                </blockquote>
                <figcaption className="mt-6 flex items-center gap-3.5 border-t border-line pt-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-navy-900 text-xs font-semibold text-white">
                    {t.name.split(" ").map((n) => n[0]).join("")}
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-navy-900">{t.name}</span>
                    <span className="block text-xs font-medium text-slate-600">{t.role}</span>
                  </span>
                </figcaption>
              </figure>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

function JournalSection() {
  return (
    <section className="bg-white py-20" data-testid="journal-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeader
            index="05"
            eyebrow="The Journal"
            title="Practical reads for founders & finance leaders."
            sub="Plain-language guides on the filings and decisions that move a business."
          />
          <Reveal delay={0.15}>
            <Link
              to="/journal"
              className="link-underline inline-flex items-center gap-2 pb-0.5 text-sm font-semibold text-navy-900"
              data-testid="journal-view-all-link"
            >
              Read the journal <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
        <Stagger className="mt-12 grid gap-5 md:grid-cols-3" gap={0.07}>
          {POSTS.map((post) => (
            <StaggerItem key={post.slug} className="h-full">
              <Link
                to={`/journal/${post.slug}`}
                data-testid={`home-journal-card-${post.slug}`}
                className="group flex h-full flex-col rounded-lg border border-line bg-white p-6 transition-all duration-200 hover:border-navy-100 hover:shadow-lift"
              >
                <span className="font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-gold-deep">{post.category}</span>
                <span className="mt-4 text-lg font-semibold leading-snug text-navy-900">{post.title}</span>
                <span className="mt-2 flex-1 text-[15px] leading-relaxed text-ink">{post.excerpt}</span>
                <span className="mt-6 font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-slate-600">
                  {post.date} · {post.readTime}
                </span>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

function FaqWithForm() {
  return (
    <section className="bg-mist py-20" data-testid="faq-form-section">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-6 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <SectionHeader
            index="06"
            eyebrow="FAQ"
            title="Clear answers, before we even meet."
          />
          <Reveal className="mt-8">
            <FaqSection items={FAQS} />
          </Reveal>
        </div>
        <div className="lg:col-span-6">
          <Reveal delay={0.1}>
            <div className="rounded-lg border border-line bg-white p-7 shadow-card sm:p-8" data-testid="home-lead-card">
              <p className="font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-gold-deep">Free callback</p>
              <h3 className="mt-3 text-2xl font-semibold text-navy-900">Talk to a real CA today.</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-ink">
                No sales pitch. Just honest advice on the right next step for your business.
              </p>
              <div className="mt-6">
                <LeadForm id="home-lead" />
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function FooterCta() {
  return (
    <section className="bg-navy-900 py-14" data-testid="cta-band">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-5 sm:px-6">
        <Reveal>
          <h2 className="max-w-xl text-2xl font-semibold text-white sm:text-3xl">
            Your first CA shouldn't feel like a gamble.
          </h2>
          <p className="mt-2 text-base text-navy-100">
            One call is enough to know whether we're the right fit.
          </p>
        </Reveal>
        <Reveal delay={0.12} className="flex flex-wrap gap-3.5">
          <a
            href={`tel:${SITE.phoneTel}`}
            className="inline-flex items-center gap-2 rounded-md bg-white px-6 py-3 text-sm font-semibold text-navy-900 transition-colors hover:bg-navy-50"
            data-testid="cta-band-call-button"
          >
            <Phone className="h-4 w-4" /> {SITE.phoneDisplay}
          </a>
          <a
            href={SITE.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-md border border-white/40 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
            data-testid="cta-band-whatsapp-button"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp now
          </a>
        </Reveal>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <>
      <Seo
        title="SAIVU Consulting Private Limited | Business, Finance, Tax & Compliance Consulting"
        description="SAIVU Consulting Private Limited — a professional consulting company with a multidisciplinary team of Chartered Accountants, MBA Finance, legal and tax professionals. Business registration, GST, income tax, compliance, CFO advisory and more for Indian businesses."
        path="/"
      />
      <Hero />
      <Marquee />
      <ServicesSection />
      <ProcessSection />
      <WhyUs />
      <Testimonials />
      <JournalSection />
      <FaqWithForm />
      <FooterCta />
    </>
  );
}
